import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const PondSensorTable = ({ reload }) => {
    const [pondSensors, setPondSensors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 1,
    });
    const pageSize = 10; // Define number of items per page

    const fetchPondSensors = useCallback(async (page = 1) => {
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/api/sensors/pondsensors?page=${page}&limit=${pageSize}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setPondSensors(response.data.data);
            setPagination({
                total: response.data.total,
                totalPages: response.data.totalPages,
            });
            setCurrentPage(page);
            setLoading(false);
        } catch (error) {
            Swal.fire("Error", "Failed to fetch pond sensor data.", "error");
            setLoading(false);
        }
    }, [pageSize]);

    useEffect(() => {
        fetchPondSensors(currentPage);
    }, [reload, currentPage, fetchPondSensors]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const getPaginationButtons = () => {
        const { totalPages } = pagination;
        const pageNumbers = [];

        let startPage = Math.max(currentPage - 2, 1);
        let endPage = Math.min(currentPage + 2, totalPages);

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) pageNumbers.push("...");
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages - 1) pageNumbers.push("...");
        if (endPage < totalPages) pageNumbers.push(totalPages);

        return pageNumbers;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center">
                <div className="flex items-center space-x-2 animate-pulse">
                    <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    <div className="text-blue-500 text-lg font-semibold">Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-6">
            <h1 className="text-3xl font-bold mb-6">Pond-wise Sensor Table</h1>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead>
                    <tr>
                        <th className="px-4 py-2 border">Pond Name</th>
                        <th className="px-4 py-2 border">Sensor Name</th>
                        <th className="px-4 py-2 border">Sensor Type</th>
                    </tr>
                    </thead>
                    <tbody>
                    {pondSensors.map((pondSensor) => (
                        <tr key={pondSensor._id}>
                            <td className="px-4 py-2 border text-center">{pondSensor.pond_id.name}</td>
                            <td className="px-4 py-2 border text-center">{pondSensor.sensor_id.name}</td>
                            <td className="px-4 py-2 border text-center">
                                {pondSensor.sensor_id.sensor_parameter_id?.sensor_name}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {pagination.total > 0 && (
                <div className="mt-6 flex justify-center items-center space-x-2">
                    <button
                        onClick={() => handlePageChange(1)}
                        className={`px-3 py-1 rounded ${currentPage === 1 ? "bg-gray-300" : "bg-gray-200 text-gray-800"}`}
                        disabled={currentPage === 1}
                    >
                        First
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        className={`px-3 py-1 rounded ${currentPage === 1 ? "bg-gray-300" : "bg-gray-200 text-gray-800"}`}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>

                    {getPaginationButtons().map((page, index) =>
                        page === "..." ? (
                            <span key={index} className="px-3 py-1">...</span>
                        ) : (
                            <button
                                key={index}
                                onClick={() => handlePageChange(page)}
                                className={`px-3 py-1 rounded ${
                                    currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-800"
                                }`}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        className={`px-3 py-1 rounded ${currentPage === pagination.totalPages ? "bg-gray-300" : "bg-gray-200 text-gray-800"}`}
                        disabled={currentPage === pagination.totalPages}
                    >
                        Next
                    </button>
                    <button
                        onClick={() => handlePageChange(pagination.totalPages)}
                        className={`px-3 py-1 rounded ${currentPage === pagination.totalPages ? "bg-gray-300" : "bg-gray-200 text-gray-800"}`}
                        disabled={currentPage === pagination.totalPages}
                    >
                        Last
                    </button>
                </div>
            )}
        </div>
    );
};

export default PondSensorTable;
